import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import SessionReminder from "@/components/SessionReminder";

// Public Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ParentRegister from "@/pages/ParentRegister";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// Student Portal
import StudentDashboard from "@/pages/student/StudentDashboard";
import SubjectPage from "@/pages/student/SubjectPage";
import TopicPage from "@/pages/student/TopicPage";
import LessonPage from "@/pages/student/LessonPage";
import GamesPage from "@/pages/student/GamesPage";
import AchievementsPage from "@/pages/student/AchievementsPage";
import ExamPracticePage from "@/pages/student/ExamPracticePage";
import ExamTakingPage from "@/pages/student/ExamTakingPage";
import MemoryMatchGame from "@/pages/student/MemoryMatchGame";
import QuickQuizGame from "@/pages/student/QuickQuizGame";
import NumberNinjaGame from "@/pages/student/NumberNinjaGame";
import WordPuzzleGame from "@/pages/student/WordPuzzleGame";
import SpellingBeeGame from "@/pages/student/SpellingBeeGame";
import ScienceLabGame from "@/pages/student/ScienceLabGame";
import SpaceMathGame from "@/pages/student/SpaceMathGame";
import WordBuilderGame from "@/pages/student/WordBuilderGame";
import FractionFrenzyGame from "@/pages/student/FractionFrenzyGame";
import GrammarGuardianGame from "@/pages/student/GrammarGuardianGame";
import LetterSoundsSafariGame from "@/pages/student/LetterSoundsSafariGame";
import BlendingBusGame from "@/pages/student/BlendingBusGame";
import RhymeTimeRocketGame from "@/pages/student/RhymeTimeRocketGame";
import PhonicsPopGame from "@/pages/student/PhonicsPopGame";
import VirtualMicroscopeGame from "@/pages/student/VirtualMicroscopeGame";
import CircuitBuilderGame from "@/pages/student/CircuitBuilderGame";
import SolarSystemGame from "@/pages/student/SolarSystemGame";
import EssayPage from "@/pages/student/EssayPage";
import WeeklyChallengesPage from "@/pages/student/WeeklyChallengesPage";
import TimesTablesPage from "@/pages/student/TimesTablesPage";
import TimesTablesLearnPage from "@/pages/student/TimesTablesLearnPage";
import TimesTablesGamePage from "@/pages/student/TimesTablesGamePage";
import ESOLVideoLessons from "@/pages/student/ESOLVideoLessons";
import ESOLLessonPage from "@/pages/student/ESOLLessonPage";
import ElevenPlusPage from "@/pages/student/ElevenPlusPage";

// Live Tutoring
import LiveTutoringPage from "@/pages/tutoring/LiveTutoringPage";
import VideoRoom from "@/pages/tutoring/VideoRoom";

// Parent Portal
import ParentDashboard from "@/pages/parent/ParentDashboard";
import ChildProgress from "@/pages/parent/ChildProgress";
import RegisterChild from "@/pages/parent/RegisterChild";

// Tutor Portal
import TutorDashboard from "@/pages/tutor/TutorDashboard";
import StudentDetail from "@/pages/tutor/StudentDetail";

// Admin Portal
import AdminDashboard from "@/pages/admin/AdminDashboard";
import UserManagement from "@/pages/admin/UserManagement";
import SubjectAllocation from "@/pages/admin/SubjectAllocation";
import TutorAssignment from "@/pages/admin/TutorAssignment";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminStudentProgress from "@/pages/admin/AdminStudentProgress";
import AdminAssessments from "@/pages/admin/AdminAssessments";
import AssessmentReport from "@/pages/admin/AssessmentReport";
import MonthlyReports from "@/pages/admin/MonthlyReports";
import AdminCurriculumBrowser from "@/pages/admin/AdminCurriculumBrowser";
import AdminSettings from "@/pages/admin/AdminSettings";
import ParentLetterPage from "@/pages/admin/ParentLetterPage";
import GrouponSignups from "@/pages/admin/GrouponSignups";
import EnrollmentsPage from "@/pages/admin/EnrollmentsPage";
import QualityDashboard from "@/pages/admin/QualityDashboard";
import SubscriptionManagement from "@/pages/admin/SubscriptionManagement";

// Public Assessment
import PublicAssessment from "@/pages/assessment/PublicAssessment";
import ParentReportPage from "@/pages/assessment/ParentReportPage";
import EnrollmentSuccessPage from "@/pages/assessment/EnrollmentSuccessPage";

// Student Analytics
import StudentAnalytics from "@/pages/student/StudentAnalytics";

// Reading Adventures
import ReadingAdventure from "@/pages/student/ReadingAdventure";
import ReadingStory from "@/pages/student/ReadingStory";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const dashboards = {
      admin: '/admin',
      tutor: '/tutor',
      parent: '/parent',
      student: '/student'
    };
    return <Navigate to={dashboards[user.role] || '/login'} replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }
  
  if (user) {
    const dashboards = {
      admin: '/admin',
      tutor: '/tutor',
      parent: '/parent',
      student: '/student'
    };
    return <Navigate to={dashboards[user.role] || '/student'} replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><ParentRegister /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Public Assessment (no auth required) */}
          <Route path="/assessment/:token" element={<PublicAssessment />} />
          <Route path="/assessment-report/:reportToken" element={<ParentReportPage />} />
          <Route path="/enrollment-success" element={<EnrollmentSuccessPage />} />
          
          {/* Student Portal */}
          <Route path="/student" element={<ProtectedRoute allowedRoles={['student']}><StudentDashboard /></ProtectedRoute>} />
          <Route path="/student/subject/:subject/:keyStage" element={<ProtectedRoute allowedRoles={['student']}><SubjectPage /></ProtectedRoute>} />
          <Route path="/student/topic/:topicId" element={<ProtectedRoute allowedRoles={['student']}><TopicPage /></ProtectedRoute>} />
          <Route path="/student/lesson/:lessonId" element={<ProtectedRoute allowedRoles={['student']}><LessonPage /></ProtectedRoute>} />
          <Route path="/student/games" element={<ProtectedRoute allowedRoles={['student']}><GamesPage /></ProtectedRoute>} />
          <Route path="/student/games/memory-match" element={<ProtectedRoute allowedRoles={['student']}><MemoryMatchGame /></ProtectedRoute>} />
          <Route path="/student/games/quick-quiz" element={<ProtectedRoute allowedRoles={['student']}><QuickQuizGame /></ProtectedRoute>} />
          <Route path="/student/games/number-ninja" element={<ProtectedRoute allowedRoles={['student']}><NumberNinjaGame /></ProtectedRoute>} />
          <Route path="/student/games/word-puzzle" element={<ProtectedRoute allowedRoles={['student']}><WordPuzzleGame /></ProtectedRoute>} />
          <Route path="/student/games/spelling-bee" element={<ProtectedRoute allowedRoles={['student']}><SpellingBeeGame /></ProtectedRoute>} />
          <Route path="/student/games/science-lab" element={<ProtectedRoute allowedRoles={['student']}><ScienceLabGame /></ProtectedRoute>} />
          <Route path="/student/games/space-math" element={<ProtectedRoute allowedRoles={['student']}><SpaceMathGame /></ProtectedRoute>} />
          <Route path="/student/games/word-builder" element={<ProtectedRoute allowedRoles={['student']}><WordBuilderGame /></ProtectedRoute>} />
          <Route path="/student/games/fraction-frenzy" element={<ProtectedRoute allowedRoles={['student']}><FractionFrenzyGame /></ProtectedRoute>} />
          <Route path="/student/games/grammar-guardian" element={<ProtectedRoute allowedRoles={['student']}><GrammarGuardianGame /></ProtectedRoute>} />
          <Route path="/student/games/letter-sounds-safari" element={<ProtectedRoute allowedRoles={['student']}><LetterSoundsSafariGame /></ProtectedRoute>} />
          <Route path="/student/games/blending-bus" element={<ProtectedRoute allowedRoles={['student']}><BlendingBusGame /></ProtectedRoute>} />
          <Route path="/student/games/rhyme-time-rocket" element={<ProtectedRoute allowedRoles={['student']}><RhymeTimeRocketGame /></ProtectedRoute>} />
          <Route path="/student/games/phonics-pop" element={<ProtectedRoute allowedRoles={['student']}><PhonicsPopGame /></ProtectedRoute>} />
          <Route path="/student/games/virtual-microscope" element={<ProtectedRoute allowedRoles={['student']}><VirtualMicroscopeGame /></ProtectedRoute>} />
          <Route path="/student/games/circuit-builder" element={<ProtectedRoute allowedRoles={['student']}><CircuitBuilderGame /></ProtectedRoute>} />
          <Route path="/student/games/solar-system" element={<ProtectedRoute allowedRoles={['student']}><SolarSystemGame /></ProtectedRoute>} />
          <Route path="/student/essay" element={<ProtectedRoute allowedRoles={['student']}><EssayPage /></ProtectedRoute>} />
          <Route path="/student/challenges" element={<ProtectedRoute allowedRoles={['student']}><WeeklyChallengesPage /></ProtectedRoute>} />
          <Route path="/student/times-tables" element={<ProtectedRoute allowedRoles={['student']}><TimesTablesPage /></ProtectedRoute>} />
          <Route path="/student/times-tables/learn/:tableNumber" element={<ProtectedRoute allowedRoles={['student']}><TimesTablesLearnPage /></ProtectedRoute>} />
          <Route path="/student/times-tables/game/:gameType/:tableNumber" element={<ProtectedRoute allowedRoles={['student']}><TimesTablesGamePage /></ProtectedRoute>} />
          <Route path="/student/esol-videos" element={<ProtectedRoute allowedRoles={['student']}><ESOLVideoLessons /></ProtectedRoute>} />
          <Route path="/student/esol" element={<ProtectedRoute allowedRoles={['student']}><ESOLVideoLessons /></ProtectedRoute>} />
          <Route path="/student/esol/:topicId" element={<ProtectedRoute allowedRoles={['student']}><ESOLLessonPage /></ProtectedRoute>} />
          <Route path="/student/achievements" element={<ProtectedRoute allowedRoles={['student']}><AchievementsPage /></ProtectedRoute>} />
          <Route path="/student/exams" element={<ProtectedRoute allowedRoles={['student']}><ExamPracticePage /></ProtectedRoute>} />
          <Route path="/student/exams/:paperId" element={<ProtectedRoute allowedRoles={['student']}><ExamTakingPage /></ProtectedRoute>} />
          <Route path="/student/11plus" element={<ProtectedRoute allowedRoles={['student']}><ElevenPlusPage /></ProtectedRoute>} />
          <Route path="/student/analytics" element={<ProtectedRoute allowedRoles={['student']}><StudentAnalytics /></ProtectedRoute>} />
          <Route path="/student/reading" element={<ProtectedRoute allowedRoles={['student']}><ReadingAdventure /></ProtectedRoute>} />
          <Route path="/student/reading/:storyId" element={<ProtectedRoute allowedRoles={['student']}><ReadingStory /></ProtectedRoute>} />
          
          {/* Parent Portal */}
          <Route path="/parent" element={<ProtectedRoute allowedRoles={['parent']}><ParentDashboard /></ProtectedRoute>} />
          <Route path="/parent/child/:childId" element={<ProtectedRoute allowedRoles={['parent']}><ChildProgress /></ProtectedRoute>} />
          <Route path="/parent/register-child" element={<ProtectedRoute allowedRoles={['parent']}><RegisterChild /></ProtectedRoute>} />
          
          {/* Tutor Portal */}
          <Route path="/tutor" element={<ProtectedRoute allowedRoles={['tutor']}><TutorDashboard /></ProtectedRoute>} />
          <Route path="/tutor/student/:studentId" element={<ProtectedRoute allowedRoles={['tutor']}><StudentDetail /></ProtectedRoute>} />
          
          {/* Admin Portal */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
          <Route path="/admin/allocate" element={<ProtectedRoute allowedRoles={['admin']}><SubjectAllocation /></ProtectedRoute>} />
          <Route path="/admin/tutors" element={<ProtectedRoute allowedRoles={['admin']}><TutorAssignment /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['admin']}><AdminAnalytics /></ProtectedRoute>} />
          <Route path="/admin/student-progress" element={<ProtectedRoute allowedRoles={['admin']}><AdminStudentProgress /></ProtectedRoute>} />
          <Route path="/admin/assessments" element={<ProtectedRoute allowedRoles={['admin']}><AdminAssessments /></ProtectedRoute>} />
          <Route path="/admin/assessments/:submissionId" element={<ProtectedRoute allowedRoles={['admin']}><AssessmentReport /></ProtectedRoute>} />
          <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['admin']}><MonthlyReports /></ProtectedRoute>} />
          <Route path="/admin/curriculum-browser" element={<ProtectedRoute allowedRoles={['admin']}><AdminCurriculumBrowser /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['admin']}><AdminSettings /></ProtectedRoute>} />
          <Route path="/admin/enrollments" element={<ProtectedRoute allowedRoles={['admin']}><EnrollmentsPage /></ProtectedRoute>} />
          <Route path="/admin/quality" element={<ProtectedRoute allowedRoles={['admin']}><QualityDashboard /></ProtectedRoute>} />
          <Route path="/admin/subscriptions" element={<ProtectedRoute allowedRoles={['admin']}><SubscriptionManagement /></ProtectedRoute>} />
          <Route path="/admin/parent-letter/:studentId" element={<ProtectedRoute allowedRoles={['admin']}><ParentLetterPage /></ProtectedRoute>} />
          <Route path="/admin/groupon-signups" element={<ProtectedRoute allowedRoles={['admin']}><GrouponSignups /></ProtectedRoute>} />
          
          {/* Admin Content Preview Routes */}
          <Route path="/admin/preview/subject/:subject/:keyStage" element={<ProtectedRoute allowedRoles={['admin']}><SubjectPage /></ProtectedRoute>} />
          <Route path="/admin/preview/topic/:topicId" element={<ProtectedRoute allowedRoles={['admin']}><TopicPage /></ProtectedRoute>} />
          <Route path="/admin/preview/lesson/:lessonId" element={<ProtectedRoute allowedRoles={['admin']}><LessonPage /></ProtectedRoute>} />
          <Route path="/admin/preview/games" element={<ProtectedRoute allowedRoles={['admin']}><GamesPage /></ProtectedRoute>} />
          <Route path="/admin/preview/games/memory-match" element={<ProtectedRoute allowedRoles={['admin']}><MemoryMatchGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/quick-quiz" element={<ProtectedRoute allowedRoles={['admin']}><QuickQuizGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/number-ninja" element={<ProtectedRoute allowedRoles={['admin']}><NumberNinjaGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/word-puzzle" element={<ProtectedRoute allowedRoles={['admin']}><WordPuzzleGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/spelling-bee" element={<ProtectedRoute allowedRoles={['admin']}><SpellingBeeGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/science-lab" element={<ProtectedRoute allowedRoles={['admin']}><ScienceLabGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/space-math" element={<ProtectedRoute allowedRoles={['admin']}><SpaceMathGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/word-builder" element={<ProtectedRoute allowedRoles={['admin']}><WordBuilderGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/fraction-frenzy" element={<ProtectedRoute allowedRoles={['admin']}><FractionFrenzyGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/grammar-guardian" element={<ProtectedRoute allowedRoles={['admin']}><GrammarGuardianGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/letter-sounds-safari" element={<ProtectedRoute allowedRoles={['admin']}><LetterSoundsSafariGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/blending-bus" element={<ProtectedRoute allowedRoles={['admin']}><BlendingBusGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/rhyme-time-rocket" element={<ProtectedRoute allowedRoles={['admin']}><RhymeTimeRocketGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/phonics-pop" element={<ProtectedRoute allowedRoles={['admin']}><PhonicsPopGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/virtual-microscope" element={<ProtectedRoute allowedRoles={['admin']}><VirtualMicroscopeGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/circuit-builder" element={<ProtectedRoute allowedRoles={['admin']}><CircuitBuilderGame /></ProtectedRoute>} />
          <Route path="/admin/preview/games/solar-system" element={<ProtectedRoute allowedRoles={['admin']}><SolarSystemGame /></ProtectedRoute>} />
          <Route path="/admin/preview/exams" element={<ProtectedRoute allowedRoles={['admin']}><ExamPracticePage /></ProtectedRoute>} />
          <Route path="/admin/preview/exams/:paperId" element={<ProtectedRoute allowedRoles={['admin']}><ExamTakingPage /></ProtectedRoute>} />
          <Route path="/admin/preview/essays" element={<ProtectedRoute allowedRoles={['admin']}><EssayPage /></ProtectedRoute>} />
          <Route path="/admin/preview/11plus" element={<ProtectedRoute allowedRoles={['admin']}><ElevenPlusPage /></ProtectedRoute>} />
          <Route path="/admin/preview/esol-videos" element={<ProtectedRoute allowedRoles={['admin']}><ESOLVideoLessons /></ProtectedRoute>} />
          <Route path="/admin/preview/esol" element={<ProtectedRoute allowedRoles={['admin']}><ESOLVideoLessons /></ProtectedRoute>} />
          <Route path="/admin/preview/esol/:topicId" element={<ProtectedRoute allowedRoles={['admin']}><ESOLLessonPage /></ProtectedRoute>} />
          <Route path="/admin/preview/times-tables" element={<ProtectedRoute allowedRoles={['admin']}><TimesTablesPage /></ProtectedRoute>} />
          <Route path="/admin/preview/times-tables/learn/:tableNumber" element={<ProtectedRoute allowedRoles={['admin']}><TimesTablesLearnPage /></ProtectedRoute>} />
          <Route path="/admin/preview/times-tables/game/:gameType/:tableNumber" element={<ProtectedRoute allowedRoles={['admin']}><TimesTablesGamePage /></ProtectedRoute>} />
          
          {/* Live Tutoring - accessible to students, tutors, and admins */}
          <Route path="/tutoring" element={<ProtectedRoute allowedRoles={['student', 'tutor', 'admin']}><LiveTutoringPage /></ProtectedRoute>} />
          <Route path="/tutoring/room/:sessionId" element={<ProtectedRoute allowedRoles={['student', 'tutor', 'admin']}><VideoRoom /></ProtectedRoute>} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" richColors />
        <PWAInstallPrompt />
        <SessionReminder />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
